import { SOURCE_TYPE, TMockDocument, TMockProduct, TMockUser, TSourceRecord } from '../models';

// Reference data offered as SELECTION OPTIONS across the prototype. This is not
// per-change data; it is the pool a user picks from (people, products,
// documents, source records). None of it is auto-filled into a change.

export const MOCK_USERS: TMockUser[] = [
  { id: 'u-keller', name: 'Dr. Anna Keller', email: 'anna.keller@example.com', jobTitle: 'Head of quality assurance' },
  {
    id: 'u-poterpin',
    name: 'Lukas Poterpin',
    email: 'lukas.poterpin@example.com',
    jobTitle: 'Regulatory affairs change manager',
  },
  { id: 'u-rossi', name: 'Elena Rossi', email: 'elena.rossi@example.com', jobTitle: 'Regulatory affairs manager' },
  { id: 'u-vogt', name: 'Martin Vogt', email: 'martin.vogt@example.com', jobTitle: 'Design engineer' },
  { id: 'u-meier', name: 'Dr. Sofia Meier', email: 'sofia.meier@example.com', jobTitle: 'PRRC' },
  { id: 'u-bauer', name: 'Thomas Bauer', email: 'thomas.bauer@example.com', jobTitle: 'Production lead' },
  { id: 'u-nowak', name: 'Julia Nowak', email: 'julia.nowak@example.com', jobTitle: 'Software quality lead' },
  {
    id: 'u-fischer',
    name: 'Michael Fischer',
    email: 'michael.fischer@example.com',
    jobTitle: 'Supplier quality engineer',
  },
];

export const MOCK_USER_NAMES: string[] = MOCK_USERS.map((user) => user.name);

// A person qualified to be a Change manager (used to populate that picker).
export const CHANGE_MANAGER_CANDIDATES: string[] = ['Lukas Poterpin', 'Dr. Anna Keller', 'Elena Rossi', 'Julia Nowak'];

export const MOCK_PRODUCT_FAMILIES: string[] = ['Cardionyx', 'CerebroTriage', 'Ivexis'];

export const MOCK_PRODUCTS: TMockProduct[] = [
  { id: 'p-cardionyx', name: 'Cardionyx', family: 'Cardionyx' },
  { id: 'p-cerebrotriage', name: 'CerebroTriage', family: 'CerebroTriage' },
  { id: 'p-ivexis', name: 'Ivexis', family: 'Ivexis' },
];

export const MOCK_PRODUCT_NAMES: string[] = MOCK_PRODUCTS.map((product) => product.name);

export const MOCK_DOCUMENTS: TMockDocument[] = [
  { id: 'd-rmf', name: 'Risk management file', documentNumber: 'RMF-001' },
  { id: 'd-ifu', name: 'Instructions for use master', documentNumber: 'IFU-014' },
  { id: 'd-dv', name: 'Design verification report', documentNumber: 'DVR-207' },
  { id: 'd-spec', name: 'Product specification', documentNumber: 'SPEC-033' },
  { id: 'd-supplier', name: 'Supplier qualification record', documentNumber: 'SUP-118' },
  { id: 'd-wi', name: 'Manufacturing work instruction', documentNumber: 'WI-402' },
  { id: 'd-gspr', name: 'GSPR checklist', documentNumber: 'GSPR-001' },
  { id: 'd-clinical', name: 'Clinical evaluation report', documentNumber: 'CER-009' },
];

export const MOCK_DOCUMENT_LABELS: string[] = MOCK_DOCUMENTS.map(
  (document) => `${document.name} (${document.documentNumber})`
);

export const MOCK_SOURCE_RECORDS: TSourceRecord[] = [
  {
    id: 's-cmp-241',
    reference: 'CMP-2026-241',
    type: SOURCE_TYPE.COMPLAINT,
    title: 'Intermittent lead connection reported by clinic',
    summary: 'Field complaint describing an intermittent lead connection on Cardionyx.',
  },
  {
    id: 's-aud-034',
    reference: 'AUD-2026-034',
    type: SOURCE_TYPE.AUDIT_FINDING,
    title: 'Supplier audit finding on incoming inspection',
    summary: 'Internal audit finding: incoming inspection records were incomplete for one supplier lot.',
    confirmedNonconformity: true,
  },
  {
    id: 's-capa-012',
    reference: 'CAPA-2026-012',
    type: SOURCE_TYPE.CAPA,
    title: 'CAPA for recurring packaging seal deviation',
    summary: 'CAPA addressing a recurring sterile-barrier seal deviation.',
  },
  {
    id: 's-pms-058',
    reference: 'PMS-2026-058',
    type: SOURCE_TYPE.PMS_SIGNAL,
    title: 'Trend signal from post-market surveillance',
    summary: 'PMS trend signal indicating a slight increase in usability observations.',
  },
  {
    id: 's-sup-077',
    reference: 'SUP-2026-077',
    type: SOURCE_TYPE.SUPPLIER_ISSUE,
    title: 'Supplier discontinues titanium grade',
    summary: 'Supplier notified discontinuation of a titanium raw-material grade.',
  },
  {
    id: 's-ncp-090',
    reference: 'NCP-2026-090',
    type: SOURCE_TYPE.NONCONFORMING_PRODUCT,
    title: 'Nonconforming product on final inspection',
    summary: 'Confirmed nonconforming product identified during final inspection.',
    confirmedNonconformity: true,
  },
  {
    id: 's-std-003',
    reference: 'STD-2026-003',
    type: SOURCE_TYPE.STANDARD_OR_REGULATION,
    title: 'ISO 10993-1 revision published',
    summary: 'A new revision of a biological-evaluation standard was published.',
  },
];

export const getSourceRecordById = (id?: string): TSourceRecord | undefined =>
  id ? MOCK_SOURCE_RECORDS.find((record) => record.id === id) : undefined;

export const getSourceRecordByReference = (reference: string): TSourceRecord | undefined =>
  MOCK_SOURCE_RECORDS.find((record) => record.reference === reference);
